import React, { memo, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "../ui/card";
import { Table, TableHeader, TableBody, TableCell, TableRow, TableHead } from "../ui/table";
import { EmptyState } from "../ui/estado-vazio";
import { SortableHeader } from "./SortableHeader";
import { cn } from "@/lib/utils";
import { ColumnState } from "./DataTable";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileCard } from "./MobileCard";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Info } from "lucide-react";
import { ResizeHandle } from "./ResizeHandle";
import { Skeleton } from "../ui/skeleton";

const getNestedValue = (obj: any, path: string) => {
  return path.split(".").reduce((acc, p) => acc?.[p], obj);
};

// Larguras variadas para que as linhas skeleton pareçam conteúdo real.
const SKELETON_WIDTHS = ["w-3/4", "w-1/2", "w-5/6", "w-2/3", "w-2/5", "w-4/5"];
const SKELETON_ROW_COUNT = 8;
const SKELETON_CARD_COUNT = 5;

/**
 * Só sinaliza carregamento depois de `delay` ms. Respostas rápidas
 * (filtro/ordenação/busca que voltam logo) não chegam a mostrar o
 * indicador, eliminando o "pisca".
 */
function useDelayedLoading(active: boolean, delay = 150) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!active) {
      setShown(false);
      return;
    }
    const timer = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(timer);
  }, [active, delay]);

  return shown;
}

// Âncora real que se estica pela linha inteira (via `position: relative` no <tr> pai),
// dando suporte nativo do navegador a "abrir em nova aba" (botão direito), ctrl/cmd-clique
// e clique do meio. Clique simples faz preventDefault e segue pela navegação SPA (onRowClick).
function RowLink({ href, onActivate }: { href: string; onActivate: () => void }) {
  return (
    <Link
      to={href}
      className="absolute inset-0 z-[1]"
      tabIndex={-1}
      aria-hidden="true"
      onClick={(e) => {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        onActivate();
      }}
    />
  );
}

// Barra de progresso indeterminada exibida no topo da tabela durante refetch.
function LoadingBar() {
  return (
    <div className="absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden bg-primary/10">
      <div className="h-full w-full origin-left animate-progress-indeterminate bg-primary" />
    </div>
  );
}

interface DataContentProps<T> {
  data: T[];
  columns: ColumnState<T>[];
  isLoading?: boolean;
  isFullscreen?: boolean;
  colProps?: { className?: string };
  sortColumn?: string | null;
  sortOrder?: "ASC" | "DESC";
  onSortChange?: (column: string, order: "ASC" | "DESC") => void;
  onRowClick?: (row: T) => void;
  onRowDoubleClick?: (row: T) => void;
  /** URL da linha (rota de detalhe). Habilita "abrir em nova aba" (botão direito/ctrl/meio-clique) via um `<a>` real sobreposto à linha. */
  getRowHref?: (row: T) => string | undefined;
  rowClassName?: (row: T) => string;
  emptyMessage?: React.ReactNode | string;
  /** Falha na busca: troca o estado vazio por um estado de erro (com retry). */
  isError?: boolean;
  errorMessage?: React.ReactNode | string;
  onRetry?: () => void;
  /** Há filtro/busca ativos: estado vazio vira "sem resultado" + limpar filtros. */
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  rowKey?: (row: T, index: number) => React.Key;
  renderSelectionColumn?: (item: T) => React.ReactNode;
  /** Larguras de coluna (persistidas no `DataTable`); chave = `col.key`. */
  columnWidths?: Record<string, number>;
  /** Inicia o arraste de redimensionamento de uma coluna. */
  onStartResize?: (e: React.MouseEvent, key: string) => void;
}

function DataContentInner<T>(props: Readonly<DataContentProps<T>>) {
  const {
    data,
    columns,
    isLoading = false,
    isFullscreen = false,
    sortColumn,
    sortOrder,
    onSortChange,
    onRowClick,
    onRowDoubleClick,
    getRowHref,
    rowClassName,
    emptyMessage = "Nenhum item encontrado.",
    isError = false,
    errorMessage = "Não foi possível carregar os dados.",
    onRetry,
    hasActiveFilters = false,
    onClearFilters,
    colProps,
    rowKey,
    renderSelectionColumn,
    columnWidths = {},
    onStartResize,
  } = props;

  const isMobile = useIsMobile();

  // Estado vazio ciente de contexto: erro > filtro sem resultado > vazio puro.
  const renderEmptyState = () => {
    if (isError) {
      return (
        <EmptyState
          message={errorMessage}
          actionLabel={onRetry ? "Tentar novamente" : undefined}
          onAction={onRetry}
        />
      );
    }
    if (hasActiveFilters) {
      return (
        <EmptyState
          message="Nenhum resultado para os filtros aplicados."
          actionLabel={onClearFilters ? "Limpar filtros" : undefined}
          onAction={onClearFilters}
        />
      );
    }
    return <EmptyState message={emptyMessage} />;
  };

  const hasData = !!data?.length;
  // Carregamento "estável": só vira true se demorar > 150ms (evita o pisca).
  const delayedLoading = useDelayedLoading(isLoading);
  // Primeira carga (sem dados): mostramos linhas skeleton.
  const showInitialSkeleton = delayedLoading && !hasData;
  // Refetch (já há dados): mantemos a tabela e mostramos a barra de progresso.
  const showProgressBar = delayedLoading && hasData;

  const processedColumns = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      accessor: col.render
        ? (item: T) => col.render!(item, onRowClick)
        : (item: T) => getNestedValue(item, col.key),

      raw: col.raw ? col.raw : (item: T) => getNestedValue(item, col.key),
      /*
       * O balão da célula serve para revelar o que truncou — nunca para repetir, e muito menos
       * para contradizer. Por isso ele **não** cai mais no `raw`: aquilo é o valor de exportação
       * (número cru, ponto decimal), e numa coluna de dinheiro o balão mostrava `510.78` por
       * cima de "R$ 510,78".
       *
       * Sem `render` próprio o que está na tela é o `raw`, então ele revela o truncado e é
       * seguro. Com `render`, só a coluna sabe o que aparece: ela declara em `cellTitle`, ou
       * fica sem balão.
       */
      cellTitle: col.cellTitle
        ? col.cellTitle
        : col.render
          ? () => ""
          : (item: T) => getNestedValue(item, col.key),
    }));
  }, [columns, onRowClick]);

  const getRowKey = (row: T, idx: number) => {
    if (rowKey) return rowKey(row, idx);
    return (row as any).id ?? (row as any)._id ?? idx;
  };

  // --- MOBILE ---
  if (isMobile) {
    // Primeira carga: cards skeleton em vez de tela vazia.
    if (showInitialSkeleton) {
      return (
        <div className="w-full space-y-3">
          {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
            <Card
              key={i}
              className="animate-fade-in space-y-3 p-4"
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              <Skeleton className="h-5 w-2/3" shimmerDelay={i * 0.1} />
              <Skeleton className="h-4 w-full" shimmerDelay={i * 0.1 + 0.08} />
              <Skeleton className="h-4 w-5/6" shimmerDelay={i * 0.1 + 0.16} />
              <Skeleton className="h-4 w-1/2" shimmerDelay={i * 0.1 + 0.24} />
            </Card>
          ))}
        </div>
      );
    }

    // Loading sem dados e ainda dentro do delay: nada (evita flash).
    if (isLoading && !hasData) return null;

    if (!isLoading && !hasData) {
      return <Card className="w-full">{renderEmptyState()}</Card>;
    }

    return (
      <div className="relative w-full space-y-3">
        {showProgressBar && <LoadingBar />}
        {data.map((item, i) => (
          <MobileCard
            key={getRowKey(item, i)}
            id={getRowKey(item, i)}
            item={item}
            columns={processedColumns}
            onRowClick={onRowClick}
            renderSelectionColumn={renderSelectionColumn}
          />
        ))}
      </div>
    );
  }

  // --- DESKTOP ---

  if (!isLoading && !hasData) {
    return <Card className="w-full">{renderEmptyState()}</Card>;
  }

  // Loading sem dados e ainda dentro do delay: nada (evita flash).
  if (isLoading && !hasData && !showInitialSkeleton) {
    return null;
  }

  return (
    <Card
      className={cn(
        "relative flex w-full flex-col overflow-hidden rounded-md border-none",
        isFullscreen && "min-h-0 flex-1",
      )}
    >
      {showProgressBar && <LoadingBar />}
      <Table containerClassName={isFullscreen ? "flex-1 min-h-0" : "max-h-[70vh]"}>
        <TableHeader className="sticky top-0 z-10 bg-muted px-0 shadow-sm">
          <TableRow>
            {renderSelectionColumn && (
              <TableHead className={cn("w-[50px]", colProps?.className)}>Selecione</TableHead>
            )}

            {processedColumns.map((col) => {
              // 2. LÓGICA DE LARGURA
              // Se tivermos uma largura no estado (drag), usamos ela.
              // Se não, usamos a largura original da prop (se houver).
              // Caso contrário, deixamos 'auto' (comportamento padrão).
              const currentWidth = columnWidths[col.key] ? `${columnWidths[col.key]}px` : col.width;

              if (col.type === "headless") {
                return <TableHead key={col.key} className={colProps?.className} />;
              }

              const content = (
                <div className="flex items-center gap-x-2">
                  <span className="truncate" title={col.title}>
                    {col.title}
                  </span>
                  {col.tooltip && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>{col.tooltip}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              );

              return (
                <React.Fragment key={col.key}>
                  {col.sortable ? (
                    <SortableHeader
                      column={col.key}
                      currentSort={sortColumn}
                      currentOrder={sortOrder}
                      onSortChange={onSortChange!}
                      className={cn("group relative whitespace-nowrap", colProps?.className)} // Adicionado relative e group
                      style={{ width: currentWidth, minWidth: currentWidth }} // Aplica largura
                    >
                      {col.title}
                      {/* 3. INSERE O HANDLE DE RESIZE */}
                      {onStartResize && (
                        <ResizeHandle onMouseDown={(e) => onStartResize(e, col.key)} />
                      )}
                    </SortableHeader>
                  ) : (
                    <TableHead
                      className={cn("group relative whitespace-nowrap", colProps?.className)}
                      style={{ width: currentWidth, minWidth: currentWidth }} // Aplica largura
                    >
                      {content}
                      {/* 3. INSERE O HANDLE DE RESIZE */}
                      {onStartResize && (
                        <ResizeHandle onMouseDown={(e) => onStartResize(e, col.key)} />
                      )}
                    </TableHead>
                  )}
                </React.Fragment>
              );
            })}
          </TableRow>
        </TableHeader>

        <TableBody>
          {showInitialSkeleton &&
            Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIdx) => (
              <TableRow
                key={`skeleton-${rowIdx}`}
                className="h-[50px] animate-fade-in border-b"
                style={{ animationDelay: `${rowIdx * 0.04}s` }}
              >
                {renderSelectionColumn && (
                  <TableCell className="py-4">
                    <Skeleton className="h-5 w-5" />
                  </TableCell>
                )}
                {processedColumns.map((col, colIdx) => (
                  <TableCell key={col.key} className="py-4">
                    <Skeleton
                      // Largura varia por linha+coluna => parece conteúdo real, não uma grade uniforme.
                      className={cn(
                        "h-5",
                        SKELETON_WIDTHS[(rowIdx + colIdx) % SKELETON_WIDTHS.length],
                      )}
                      // Onda diagonal: o brilho "varre" a tabela de cima-esquerda p/ baixo-direita.
                      shimmerDelay={(rowIdx + colIdx) * 0.08}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}

          {!showInitialSkeleton &&
            data.map((item, i) => {
              const rowHref = getRowHref?.(item);
              return (
                <TableRow
                  key={getRowKey(item, i)}
                  className={cn(
                    "transition-colors hover:bg-muted/40",
                    onRowClick && "cursor-pointer",
                    rowClassName ? rowClassName?.(item) : undefined,
                  )}
                  onClick={!rowHref && onRowClick ? () => onRowClick(item) : undefined}
                  onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(item) : undefined}
                >
                  {renderSelectionColumn && <TableCell>{renderSelectionColumn(item)}</TableCell>}
                  {processedColumns?.map((col) => {
                    // 4. APLICA A LARGURA NA CÉLULA TAMBÉM
                    // Isso garante que a coluna obedeça ao cabeçalho
                    const currentWidth = columnWidths[col.key]
                      ? `${columnWidths[col.key]}px`
                      : col.width;
                    // Ações/menus ficam de fora do link — o usuário precisa clicar neles normalmente,
                    // sem disparar a navegação da linha.
                    const isInteractiveCol = col.type === "headless";
                    return (
                      <TableCell
                        key={col.key}
                        className={cn(
                          "relative whitespace-nowrap py-3",
                          !columnWidths[col.key] && col.width,
                        )} // Mantém classe Tailwind se não houver resize manual
                        style={{
                          width: currentWidth,
                          minWidth: currentWidth,
                          maxWidth: currentWidth,
                        }} // Força layout
                        title={typeof col.cellTitle(item) === "string" ? col.cellTitle(item) : ""}
                      >
                        {/* Uma âncora por célula (não uma só cobrindo a linha inteira): cada `<td>`
                            é `relative`, então o `absolute inset-0` do link é 100% contido nela, sem
                            depender do <tr> como container — evita inconsistência entre navegadores. */}
                        {rowHref && !isInteractiveCol && (
                          <RowLink href={rowHref} onActivate={() => onRowClick?.(item)} />
                        )}
                        <div
                          className={cn(
                            "w-full truncate",
                            // O link da linha é um overlay por cima da célula. Deixamos o conteúdo
                            // "transparente" ao mouse (o clique atravessa e navega), reativando só
                            // os elementos interativos — popovers, triggers, PersonBadge — que ficam
                            // acima do overlay e recebem o clique normalmente.
                            rowHref &&
                              !isInteractiveCol &&
                              "pointer-events-none relative z-[2] [&_[data-interactive]]:pointer-events-auto [&_[role=button]]:pointer-events-auto [&_a]:pointer-events-auto [&_button]:pointer-events-auto [&_input]:pointer-events-auto",
                          )}
                        >
                          {" "}
                          {/* Garante truncate correto */}
                          {col?.accessor(item) ?? <span className="text-muted-foreground">-</span>}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </Card>
  );
}

export const DataContent = memo(DataContentInner) as typeof DataContentInner;
